import SectionSum from "core/section-sum/SectionSum";
import SectionTitle from "core/section-title/SectionTitle";
import _ from "lodash";
import { useEffect, useState } from "react";
import { FIELDS_TYPES, MODULES_TYPES } from "constants/constants";
import {
  calculateSum,
  displayStructure,
  flattenTree,
  formatModuleName,
  initForm,
  initModuleList,
  filterData,
  formatListLabel,
  getBalancesheetFields,
  getBalancesheetModuleClassNames,
  onChangeStructure,
} from "utils/balancesheetHelper";

const MissionBalancesheetContent = ({
  data,
  readOnly,
  errors,
  form,
  setForm,
  dynamicTable,
  isValidated,
  clearError,
  isExport,
}) => {
  const [evalSum, setEvalSum] = useState({ values: {}, total: 0 });

  const [activeFields, setActiveFields] = useState([]);

  const [modules, setModules] = useState([]);
  const [hiddenField, setHiddenField] = useState({});
  const [rows, setRows] = useState({});

  useEffect(() => {
    if (data?.balancesheet) {
      //STEP-1 Flatten all modules and submodules
      setModules(_.drop(flattenTree(data?.balancesheet), 1));

      //STEP-2 MANAGE BASIC MODULES
      //Get modules fields
      const fields = getBalancesheetFields(flattenTree(data?.balancesheet));

      //STEP-3 MANAGE LIST MODULES
      //Manage Module
      const moduleListfields = _.chain(
        _.drop(flattenTree(data?.balancesheet), 1)
      )
        .filter((elm) => elm?.type === MODULES_TYPES.LIST)
        .map("fields")
        .flattenDeep()
        .value();
      // get default active field for list module
      setActiveFields(
        moduleListfields.map((elm) => `_${elm?.position}_${elm?.value || "1"}`)
      );
      const result = _.chain(moduleListfields)
        .reduce((prev, curr) => {
          const defaultVal = filterData({
            id: `${curr.field_id}_1`,
            data: fields,
          });
          return {
            ...prev,
            [formatListLabel(curr.label)]:
              _.toString(defaultVal).trim() !== ""
                ? _.chain(defaultVal).replace(",", ".").toNumber().value()
                : _.chain(JSON.parse(curr.options)?.values[0]?.rval)
                    .replace(",", ".")
                    .toNumber()
                    .value(),
          };
        }, {})
        .value();
      //Get hidden field that calculates the result of module LIST
      setHiddenField(
        _.chain(fields)
          .filter((elm) => elm.type === FIELDS_TYPES.HIDDEN)
          .first()
          .get("field_id")
          .value()
      );
      // Module list SUM
      setEvalSum({
        values: result,
        total: _.chain(Object.keys(result))
          .map((key) => result[key])
          .sum()
          .value(),
      });

      //STEP-4 INITIALIZE EVALUATION FORM
      setForm((old) => ({
        ...old,
        fields: [
          ...initForm({
            fields: _.chain(_.drop(flattenTree(data?.balancesheet), 1))
              .filter(
                (elm) =>
                  elm?.type !== MODULES_TYPES.LIST &&
                  elm?.type !== MODULES_TYPES.TABLE
              )
              .map("fields")
              .flatten()
              .value(),
          }),
          ...initModuleList({
            fields: moduleListfields,
          }),
        ],
      }));
    }
  }, [data?.balancesheet]);

  const getActiveField = (val) => {
    const startVal = _.chain(val).split("_", 2).join("_").value();
    const index = _.findIndex(activeFields, (elm) =>
      _.startsWith(elm, startVal)
    );
    if (index === -1) {
      setActiveFields((old) => [...old, val]);
    } else {
      setActiveFields((old) =>
        old.map((elm) => (_.startsWith(elm, startVal) ? val : elm))
      );
    }
  };

  const onChangeTableValues = (vals) => {
    dynamicTable.current = vals;
  };

  const manageSum = (info) => {
    setEvalSum((old) => {
      const values = {
        ...old.values,
        [info?.val?.id]: _.chain(info?.rval)
          .replace(",", ".")
          .toNumber()
          .value(),
      };
      return { ...old, total: calculateSum(values), values };
    });
  };

  //change hidden file (calculates sum) when changing module List
  useEffect(() => {
    setForm((old) => ({
      ...old,
      fields: _.map(old.fields, (elm) =>
        elm?.field_id === hiddenField
          ? { ...elm, value: `${evalSum?.total}` }
          : elm
      ),
    }));
  }, [evalSum, hiddenField]);

  return modules.map((elm) => (
    <div
      className={getBalancesheetModuleClassNames({ module: elm })}
      data-module-type={elm?.type}
      key={elm?.module_id}
    >
      {elm?.type === FIELDS_TYPES.FIELD_SUM ? (
        <SectionSum label={"Total"} value={evalSum.total} />
      ) : (
        elm?.module_title && (
          <>
            <SectionTitle
              key={elm?.module_id}
              title={elm?.module_title}
              icon={elm?.structure}
            />
            <br />
          </>
        )
      )}
      {/* DISPLAY FIELDS FOR EACH MODULE */}
      {elm?.fields?.length > 0 && elm?.structure && (
        <div className="display-fields">
          {displayStructure({
            moduleId: elm?.module_id,
            activeFields,
            values: form?.fields,
            dynamicTable,
            isValidated,
            errors: errors || {},
            fields: elm?.fields,
            getActiveField,
            getSelectedValue: manageSum,
            moduleName: formatModuleName(elm?.module_title),
            onChange: (val) => {
              onChangeStructure({ val, setForm, form });
              if (clearError && val?.id) {
                const errorId = val.id.includes("_") ? val.id : `${val.id}_1`;
                clearError(errorId);
              }
            },
            onChangeTableValues,
            structure: elm?.structure,
            type: elm?.type,
            readOnly,
            isExport,
            rows: rows[elm?.module_id] || [],
            setRows: (newRows) =>
              setRows((prev) => ({ ...prev, [elm?.module_id]: newRows })),
          })}
        </div>
      )}
    </div>
  ));
};

export default MissionBalancesheetContent;
